<template>
  <div class="so-stock-page">
    <Tabs :value="activeTab" @update:value="(val) => activeTab = val as number">
      <TabList>
        <Tab :value="0">{{ t('Estoque') }}</Tab>
        <Tab :value="1">{{ t('Catálogo') }}</Tab>
      </TabList>

      <TabPanels>
        <TabPanel :value="0">
          <!-- Low Stock Alert -->
          <div v-if="lowStockItems.length > 0" class="low-stock-alert">
            <inline-message severity="warn" :closable="false">
              <i class="pi pi-exclamation-triangle"></i>
              {{ t('Atenção') + ': ' + lowStockItems.length + ' ' + t('produtos com estoque baixo') }}
            </inline-message>
          </div>

          <SOStockTable ref="stockTableRef" @edit="handleEdit" @adjust="handleAdjust" @movements="handleMovements" />
        </TabPanel>

        <TabPanel :value="1">
          <div class="stock-toolbar">
            <Button
              :label="t('Novo Produto')"
              icon="pi pi-plus"
              size="small"
              severity="primary"
              data-testid="stock-new-product-btn"
              @click="handleNewProduct"
            />
          </div>
          <SOProductsTable @edit="handleEdit" />
        </TabPanel>
      </TabPanels>
    </Tabs>

    <SOProductImageGalleryModal />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useSOStock } from '../../composables/useSOStock';
import SOStockTable from './SOStockTable.vue';
import SOProductsTable from './SOProductsTable.vue';
import SOProductImageGalleryModal from './SOProductImageGalleryModal.vue';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface StockTableInstance {
  loadData: () => void;
}

interface LowStockItem {
  [key: string]: unknown;
}

interface Props {
  translations?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  translations: () => ({})
});

// Get i18n composable
const { t } = useI18n();

// Use composables
const { fetchLowStock } = useSOStock();

// Reactive data
const activeTab = ref<number>(0);
const stockTableRef = ref<StockTableInstance | null>(null);
const lowStockItems = ref<LowStockItem[]>([]);

// Methods
const loadLowStock = async (): Promise<void> => {
  log('SOStockPage', 'loadLowStock', 'Loading low stock items');
  try {
    lowStockItems.value = await fetchLowStock() as unknown as LowStockItem[];
    log('SOStockPage', 'loadLowStock', 'Low stock items loaded', lowStockItems.value.length);
  } catch (err: unknown) {
    logError('SOStockPage', 'loadLowStock', 'Error loading low stock', err);
    lowStockItems.value = [];
  }
};

const loadData = async (): Promise<void> => {
  log('SOStockPage', 'loadData', 'Reloading data');
  await loadLowStock();
  if (stockTableRef.value) {
    await stockTableRef.value.loadData();
  }
};

const handleAdjust = (productId: Id, franchiseId?: Id): void => {
  log('SOStockPage', 'handleAdjust', 'Opening adjust modal', { productId, franchiseId });
  eventBus.emit('modal.so-stock-adjust.open', { productId, franchiseId });
};

const handleMovements = (productId: Id): void => {
  log('SOStockPage', 'handleMovements', 'Opening movements modal for product', productId);
  eventBus.emit('modal.so-stock-movements.open', { productId });
};

const handleEdit = (productId: Id): void => {
  log('SOStockPage', 'handleEdit', 'Opening edit modal for product', productId);
  eventBus.emit('modal.so-stock-create.open', { productId });
};

const handleNewProduct = (): void => {
  log('SOStockPage', 'handleNewProduct', 'Opening new product modal');
  eventBus.emit('modal.so-stock-create.open');
};

const handleStockAdjusted = (): void => {
  log('SOStockPage', 'handleStockAdjusted', 'Stock adjusted, reloading data');
  loadLowStock();
  if (stockTableRef.value) {
    stockTableRef.value.loadData();
  }
};

// Lifecycle
onMounted(() => {
  log('SOStockPage', 'mounted', 'Component mounted');
  loadLowStock();

  eventBus.on('so-stock.adjusted', handleStockAdjusted);
});

onBeforeUnmount(() => {
  log('SOStockPage', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('so-stock.adjusted', handleStockAdjusted);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleAdjust,
  handleMovements
});
</script>

<style scoped>
.so-stock-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.low-stock-alert {
  margin-bottom: 0.5rem;
}

.low-stock-alert .pi-exclamation-triangle {
  margin-right: 0.5rem;
}

.stock-toolbar {
  display: flex;
  gap: 0.5rem;
}
</style>
